import { ParsedUrlQuery } from 'querystring';
import { GetServerSideProps, GetServerSidePropsContext, Redirect } from 'next';
import { AcceptedQueryTypes } from '@commercetools/frontend-sdk/lib/types/Query';
import isAuthRequired from 'lululemon-b2b-utils/is-auth-required';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetAccountType } from 'shared/types/account/Account';
import { PASSWORD_EXPIRED_THRESHOLD } from 'helpers/constants/common';
import { UNPROTECTED_ROUTES } from 'helpers/constants/unprotectedRoutes';
import Logger from 'helpers/logger';
import { calculateDaysFromToday } from 'helpers/utils/datePicker';
import { parseUrl } from 'helpers/utils/parseUrl';
import { setSkipPasswordCheckCookie, isSkipPasswordCheckCookieActive } from 'helpers/utils/cookieHelpers';
import { FEATURE_FLAG_LIST } from 'hooks/useFeatureFlags';
import { sdk } from 'sdk';
import { StatusCode } from 'ui/checkout/utils/constant';
import MetaTitle from 'ui/meta-title';
import { createClient, ResponseError } from 'frontastic';
import { FrontasticRenderer } from 'frontastic/lib/renderer';
import { tastics } from 'frontastic/tastics';
import styles from './slug.module.css';
import { Log } from '../helpers/errorLogger';

type SlugProps = {
  // This needs an overhaul. Can be too many things in my opinion (*Marcel)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // data: RedirectResponse | PageDataResponse | ResponseError | { ok: string; message: string } | string;
  locale: string;
};

export default function Slug({ data }: SlugProps) {
  if (!data || typeof data === 'string') {
    return (
      <>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-gray-900">Internal Error</h1>
        <p className="mt-2 text-lg">{data}</p>
        <p className="mt-2 text-lg">Check the logs of your Frontastic CLI for more details.</p>
      </>
    );
  }

  if (!data?.ok && data?.message) {
    return (
      <>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-gray-900">Internal Error</h1>
        <p className="mt-2 text-lg">{data.message}</p>
        <p className="mt-2 text-lg">Check the logs of your Frontastic CLI for more details.</p>
      </>
    );
  }

  return (
    <>
      <MetaTitle
        title={data?.pageFolder?.configuration?.seoTitle}
        description={data?.pageFolder?.configuration?.seoDescription}
      />
      <FrontasticRenderer data={data} tastics={tastics} wrapperClassName={styles.gridWrapper} />
    </>
  );
}

const isFeatureFlagEnabled = (data: any, featureFlag: string) => {
  if (!data?.data?.dataSources) return false; // Ensure dataSources exists

  const dataSourceKeys = Object.keys(data.data.dataSources);
  if (dataSourceKeys.length === 0) return false; // No data sources available

  // Check master feature flags
  const masterFlags = data.data.dataSources?.__master?.featureFlags ?? [];
  const otherFlags = data.data.dataSources[dataSourceKeys[0]]?.featureFlags ?? [];

  const allFlags = [...masterFlags, ...otherFlags];

  return allFlags.find((f: any) => f.name === featureFlag)?.isEnabled;
};

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext<ParsedUrlQuery>) => {
  const { params, locale, query, req, res, resolvedUrl } = context;
  sdk.configureForNext(locale as string);

  const protocol = req.headers.referer?.split('://')[0] || 'https';
  const baseUrl = `${protocol}://${req.headers.host}`;
  const serverUrl = `${baseUrl}${resolvedUrl}`;

  const frontastic = createClient();
  req.headers['frontastic-env'] = 'dev';
  req.headers['access-control-allow-origin'] = `https://${req.headers['host']}`;
  req.headers['x-lll-access-token'] = process.env.SSR_AKAMAI_TOKEN;
  if (process.env.FRONTASTIC_ENV) {
    req.headers['frontastic-env'] = process.env.FRONTASTIC_ENV;
  }

  // Handle password expiry when the password is expired.
  const realTimeAccountData = await sdk.callAction<GetAccountType>({
    actionName: 'account/getAccount',
    serverOptions: {
      req,
    },
    query: {
      fetchAccountFromCT: true,
    },
  });

  // Ensure data is accessed only when there is no error
  const isLoggedIn = realTimeAccountData.isError ? false : realTimeAccountData.data.loggedIn;

  const [frontasticData, categoriesData] = await Promise.allSettled([
    frontastic.getRouteData(params?.slug as string[], query as AcceptedQueryTypes, req, res),
    isLoggedIn && frontastic.getCategories(req, res),
  ]);

  const data = frontasticData.status === 'fulfilled' && frontasticData.value;
  const categories = categoriesData.status === 'fulfilled' && categoriesData.value;

  const numberOfDaysPassed =
    !realTimeAccountData.isError && realTimeAccountData?.data?.loggedIn
      ? calculateDaysFromToday(realTimeAccountData.data?.account?.custom?.fields?.password_created_at)
      : 0;

  const isPasswordExpired = numberOfDaysPassed >= PASSWORD_EXPIRED_THRESHOLD;

  const { path: fullPath, query: parsedQueryParams } = parseUrl(resolvedUrl);
  const lvp = parsedQueryParams.lvp ? decodeURIComponent(parsedQueryParams.lvp) : resolvedUrl;

  const hasChangePasswordNotification = parsedQueryParams.changePasswordNotification === 'true';
  const skipCookieActive = isSkipPasswordCheckCookieActive(req.headers.cookie);
  const skipPasswordCheck = isLoggedIn && (hasChangePasswordNotification || skipCookieActive);

  const isPasswordExpiredParam = parsedQueryParams.passwordExpired === 'true';

  if (isLoggedIn && !isPasswordExpired && resolvedUrl.startsWith('/change-password') && isPasswordExpiredParam) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    };
  }

  if (isLoggedIn && hasChangePasswordNotification) {
    setSkipPasswordCheckCookie(req, res);
  }

  if (realTimeAccountData.isError && isFeatureFlagEnabled(data, FEATURE_FLAG_LIST.LOGOUT_FIX_BUSINESS_UNIT_CHANGE)) {
    await sdk.callAction<GetAccountType>({
      actionName: 'account/logout',
      serverOptions: {
        req,
      },
    });
  }

  if (
    !realTimeAccountData.isError &&
    realTimeAccountData?.data &&
    isFeatureFlagEnabled(data, FEATURE_FLAG_LIST.ORDER_ON_BEHALF_BU_SELECTION)
  ) {
    if (resolvedUrl === '/business-unit') {
      const companyProfile = realTimeAccountData?.data?.account?.companyProfile;
      const hasCompanyProfile = Boolean(companyProfile?.businessUnitKey);

      if (hasCompanyProfile) {
        return {
          redirect: {
            destination: '/',
            permanent: false,
          },
        };
      }
    }
  }

  let hasCoordinatorRole = false;

  try {
    const businessUnitInfo = await sdk.callAction({
      actionName: 'businessUnit/fetchBusinessUnitInfo',
      serverOptions: { req },
    });

    if (businessUnitInfo && !businessUnitInfo.isError && businessUnitInfo.data) {
      const businessUnitData = (businessUnitInfo?.data as any) ?? {};
      const businessUnits = Array.isArray(businessUnitData.businessUnits) ? businessUnitData.businessUnits : [];

      const isBUSelectionEnabled = isFeatureFlagEnabled(data, FEATURE_FLAG_LIST.ORDER_ON_BEHALF_BU_SELECTION);
      const isCompanyProfileMissing = !businessUnitData?.key;
      hasCoordinatorRole = businessUnits.length > 0;

      const isBusinessUnitPage = resolvedUrl.startsWith('/business-unit');
      const isChangePasswordPage = resolvedUrl.startsWith('/change-password');

      const shouldRedirect =
        !isBusinessUnitPage &&
        !isChangePasswordPage &&
        !isPasswordExpired &&
        ((isBUSelectionEnabled && hasCoordinatorRole && isCompanyProfileMissing) ||
          (businessUnits.length > 1 && !businessUnitData?.key));

      if (shouldRedirect) {
        return {
          redirect: {
            destination: '/business-unit',
            permanent: false,
          },
        };
      }
    }
  } catch (err) {
    Logger.error(`Failed to fetch business unit info: ${JSON.stringify(err)}`);
  }

  //TODO: CHECK UNDER MAINTENANCE ALSO WORKS
  //FOLLOW PATTERN OF CHANGE-PASSWORD PAGE
  if (isFeatureFlagEnabled(data, FEATURE_FLAG_LIST.UNDER_MAINTENANCE)) {
    return {
      redirect: {
        destination: '/under-maintenance',
        permanent: false,
      } as Redirect,
    };
  }

  if (
    !skipPasswordCheck &&
    isLoggedIn &&
    isPasswordExpired &&
    resolvedUrl.startsWith('/change-password') &&
    !parsedQueryParams.passwordExpired
  ) {
    let enhancePasswordFeatureEnabled = false;

    try {
      const featureFlagsResponse = await sdk.callAction({
        actionName: 'account/getFeatureFlagList',
        serverOptions: { req },
      });

      if (!featureFlagsResponse.isError && featureFlagsResponse.data) {
        const flags = featureFlagsResponse.data as any;
        const enhanceFlag = flags.find((f: any) => f.name === FEATURE_FLAG_LIST.ENHANCE_CHANGE_PASSWORD_PAGE);
        enhancePasswordFeatureEnabled = enhanceFlag?.isEnabled ?? false;
      }
    } catch (err) {
      Logger.error(`Failed to fetch feature flags for direct change-password visit ${err}`);
    }

    if (enhancePasswordFeatureEnabled) {
      return {
        redirect: {
          destination: `/change-password?passwordExpired=true`,
          permanent: false,
        } as Redirect,
      };
    }
  }

  if (
    !skipPasswordCheck &&
    isLoggedIn &&
    isPasswordExpired &&
    !resolvedUrl.startsWith('/change-password') &&
    !resolvedUrl.startsWith('/forgot-password')
  ) {
    let enhancePasswordFeatureEnabled = false;

    try {
      const featureFlagsResponse = await sdk.callAction({
        actionName: 'account/getFeatureFlagList',
        serverOptions: { req },
      });

      if (!featureFlagsResponse.isError && featureFlagsResponse.data) {
        const flags = featureFlagsResponse.data as any;
        const enhanceFlag = flags.find((f: any) => f.name === FEATURE_FLAG_LIST.ENHANCE_CHANGE_PASSWORD_PAGE);
        enhancePasswordFeatureEnabled = enhanceFlag?.isEnabled ?? false;
      }
    } catch (err) {
      Logger.error(`Failed to fetch feature flags for password expiry ${err}`);
    }

    if (enhancePasswordFeatureEnabled) {
      return {
        redirect: {
          destination: `/change-password?passwordExpired=true`,
          permanent: false,
        } as Redirect,
      };
    } else {
      return {
        redirect: {
          destination: `/change-password?lvp=${lvp}`,
          permanent: false,
        } as Redirect,
      };
    }
  }

  const authRequired = isAuthRequired(fullPath);

  if (authRequired && !isLoggedIn) {
    const redirectTarget = fullPath === '/' ? UNPROTECTED_ROUTES.LOGIN : UNPROTECTED_ROUTES.SESSION_TIMEOUT;

    const destinationUrl = new URL(`/${redirectTarget}`, baseUrl);

    destinationUrl.searchParams.set('lvp', lvp);

    return {
      redirect: {
        destination: destinationUrl.pathname + destinationUrl.search,
        permanent: false,
      },
    };
  }

  if (data) {
    if (data instanceof ResponseError && data.getStatus() == StatusCode.NOT_FOUND) {
      Logger.error(`${JSON.stringify(data)}`);
      return {
        notFound: true,
      };
    } else if (typeof data === 'object' && 'target' in data && 'statusCode' in data) {
      return {
        redirect: {
          destination: data.target,
          permanent: false,
          statusCode: data.statusCode,
        } as Redirect,
      };
    }
  }

  //page does not exist in studio
  if (Object.keys(data).length === 0) {
    return {
      notFound: true,
    };
  }

  if (data instanceof Error) {
    // @TODO: Render nicer error page in debug mode, which shows the error to
    // the developer and also outlines how to debug this (take a look at
    // frontastic-CLI).
    Log.error(new Error('Error retrieving data: '), data);
    return {
      notFound: true,
    };
  }

  if (typeof data === 'string') {
    return {
      props: {
        data: { error: data },
        error: data,
      },
    };
  }

  const serverState = null;

  return {
    props: {
      data: data ? { ...data, categories, serverUrl, serverState } : null,
      hasCoordinatorRole,
      locale: locale,
      ...(await serverSideTranslations(locale as string, [
        'common',
        'cart',
        'product',
        'payment',
        'checkout',
        'account',
        'customer-support',
        'error',
        'success',
        'wishlist',
        'newsletter',
        'orders',
        'thank-you',
        'region',
        'inventory-grid',
      ])),
    },
  };
};
